import React, { useLayoutEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import {
  HaklaiStyledCell as StyledCell,
  HaklaiStyledHeaderCell as StyledHeaderCell,
  HaklaiStyledNameCell as StyledNameCell,
  HaklaiStyledNameHeaderCell as StyledNameHeaderCell,
  haklaiTableWrapperBaseStyles
} from "../calc/shared/table/haklaiTableShared";
import HaklaiResults2990Panel from "./form2990/HaklaiResults2990Panel";
import {
  HAKLAI_ANALYSIS_RESULTS_LABELS,
  HAKLAI_ANALYSIS_RESULTS_MESSAGES
} from "./haklaiAnalysisResultsTexts";

/**
 * Owns the Haklai analysis result screen.
 *
 * Responsibilities:
 * - renders calculation summaries returned from analysis
 * - exposes the generated 2990 form panel
 * - keeps result formatting separate from the editable calculator sections
 */

const PROPERTY_TYPE_LABELS = {
  residential: HAKLAI_ANALYSIS_RESULTS_LABELS.residential,
  commercial: HAKLAI_ANALYSIS_RESULTS_LABELS.commercial,
  commercialBg: HAKLAI_ANALYSIS_RESULTS_LABELS.commercial,
  landAndShared: HAKLAI_ANALYSIS_RESULTS_LABELS.landAndShared
};

const columns = [
  { key: "propertyType", label: HAKLAI_ANALYSIS_RESULTS_LABELS.propertyType, align: "right" },
  { key: "nominalAcquisitionPrice", label: HAKLAI_ANALYSIS_RESULTS_LABELS.nominalAcquisitionPrice },
  { key: "indexedAcquisitionPrice", label: HAKLAI_ANALYSIS_RESULTS_LABELS.indexedAcquisitionPrice },
  { key: "saleValue", label: HAKLAI_ANALYSIS_RESULTS_LABELS.saleValue },
  { key: "indexedExpenses", label: HAKLAI_ANALYSIS_RESULTS_LABELS.indexedExpenses },
  { key: "depreciation", label: HAKLAI_ANALYSIS_RESULTS_LABELS.depreciation },
  { key: "payableTax", label: HAKLAI_ANALYSIS_RESULTS_LABELS.payableTax }
];

const useStyles = makeStyles((theme) => ({
  page: {
    direction: "rtl",
    paddingTop: theme.spacing(2)
  },
  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3)
  },
  backButton: {
    minWidth: 150,
    padding: "6px 18px",
    "& .MuiButton-label": {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      lineHeight: 1
    }
  },
  backButtonIcon: {
    fontSize: "1.25rem",
    marginTop: 1
  },
  tableWrapper: {
    ...haklaiTableWrapperBaseStyles,
    overflowX: "auto"
  },
  tableInner: {
    minWidth: 980,
    tableLayout: "fixed"
  },
  resultRow: {
    "& > .MuiTableCell-root": {
      paddingTop: 12,
      paddingBottom: 12
    }
  },
  propertyColumn: {
    width: 210
  },
  amountColumn: {
    width: 128
  },
  headerLabelText: {
    fontWeight: 700,
    lineHeight: 1.2
  },
  bodyText: {
    fontWeight: 500,
    lineHeight: 1.25
  },
  propertyTextBlock: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 4
  },
  propertyMainText: {
    fontWeight: 600,
    lineHeight: 1.35
  },
  propertyMetaText: {
    color: "#637a8f",
    lineHeight: 1.2
  },
  amountText: {
    direction: "ltr",
    unicodeBidi: "plaintext"
  },
  totalText: {
    fontWeight: 700
  },
  summarySection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2.25),
    borderRadius: 20,
    border: "1px solid rgba(47, 111, 136, 0.35)",
    backgroundColor: "#ffffff",
    boxShadow: "0 12px 28px rgba(16, 42, 67, 0.06)"
  },
  resultTitleBar: {
    margin: theme.spacing(-2.25, -2.25, 2, -2.25),
    padding: theme.spacing(1.1, 2.25),
    borderRadius: "20px 20px 0 0",
    backgroundColor: "#34748c"
  },
  resultTitleText: {
    fontWeight: 800,
    color: "#ffffff",
    marginBottom: 0
  },
  summaryCardsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5)
  },
  summaryCard: {
    flex: "1 1 210px",
    minWidth: 0,
    borderRadius: 16,
    border: "1px solid rgba(23, 50, 77, 0.08)",
    background: "linear-gradient(180deg, rgba(248, 251, 253, 1) 0%, rgba(255, 255, 255, 1) 100%)",
    padding: theme.spacing(1.75)
  },
  totalSummaryCard: {
    border: "2px solid #ffad00",
    boxShadow: "inset 0 0 0 1px rgba(255, 173, 0, 0.22), 0 8px 18px rgba(255, 173, 0, 0.12)",
    background: "linear-gradient(180deg, rgba(255, 250, 239, 1) 0%, rgba(255, 255, 255, 1) 100%)"
  },
  summaryCardLabel: {
    color: "#5c7385",
    fontWeight: 600,
    marginBottom: theme.spacing(0.75)
  },
  summaryCardValue: {
    fontWeight: 800,
    fontSize: "1.2rem",
    color: "#17324d",
    direction: "ltr",
    unicodeBidi: "plaintext"
  },
  docsWrap: {
    marginTop: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(2.5)
    }
  },
  calcDetailsSection: {
    marginTop: theme.spacing(2.5),
    padding: theme.spacing(2.25),
    borderRadius: 20,
    border: "1px solid rgba(47, 111, 136, 0.35)",
    backgroundColor: "#ffffff",
    boxShadow: "0 12px 28px rgba(16, 42, 67, 0.06)"
  },
  calcDetailsCardsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5)
  },
  calcDetailsCard: {
    flex: "1 1 210px",
    minWidth: 0,
    borderRadius: 16,
    border: "1px solid rgba(23, 50, 77, 0.08)",
    background: "linear-gradient(180deg, rgba(248, 251, 253, 1) 0%, rgba(255, 255, 255, 1) 100%)",
    padding: theme.spacing(1.75)
  },
  calcDetailsCardLabel: {
    color: "#5c7385",
    fontWeight: 600,
    marginBottom: theme.spacing(0.75)
  },
  calcDetailsCardValue: {
    fontWeight: 800,
    fontSize: "1.2rem",
    color: "#17324d",
    direction: "ltr",
    unicodeBidi: "plaintext"
  }
}));

function formatCurrency(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "-";
  return numericValue.toLocaleString("he-IL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
}

function formatWholeNumber(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "-";
  return numericValue.toLocaleString("he-IL", {
    maximumFractionDigits: 0
  });
}

function formatPercent(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "-";
  const normalizedPercent = Math.abs(numericValue) <= 1 ? numericValue * 100 : numericValue;
  return `${formatWholeNumber(normalizedPercent)}%`;
}

function getPropertyDisplay(row) {
  const typeLabel = PROPERTY_TYPE_LABELS[row.propertyType] || row.propertyType || HAKLAI_ANALYSIS_RESULTS_LABELS.genericAsset;
  return row.propertyLabel ? `${typeLabel} - ${row.propertyLabel}` : typeLabel;
}

function getPropertyExemptionLabel(row, haklaiDraftPreCalc) {
  const ptorType = haklaiDraftPreCalc?.ptorType || "none";
  const hasTzinunOnPtor = haklaiDraftPreCalc?.hasTzinunOnPtor === true;

  if (row?.propertyKey !== "residence_1" || ptorType === "none") {
    return "";
  }

  if (ptorType === "single" && hasTzinunOnPtor) {
    return "";
  }

  if (ptorType === "single") {
    return HAKLAI_ANALYSIS_RESULTS_LABELS.singleApartmentExemption;
  }

  if (ptorType === "inheritance") {
    return HAKLAI_ANALYSIS_RESULTS_LABELS.inheritanceExemption;
  }

  return "";
}

function getDataPerPart(mainCalc) {
  return Array.isArray(mainCalc?.calculatedData?.dataPerPart) ? mainCalc.calculatedData.dataPerPart : [];
}

function getMaximalRabinovich(mainCalc) {
  return getDataPerPart(mainCalc).reduce((maxValue, part) => Math.max(maxValue, part.discountRabPercent || 0), 0);
}

export function getHaklaiResultsPrisaData(mainCalc) {
  return getDataPerPart(mainCalc).reduce(
    (acc, part) => {
      const prisaYears = Array.isArray(part.prisaData)
        ? part.prisaData.reduce((maxYears, personPrisaData) => (
          Math.max(maxYears, Array.isArray(personPrisaData) ? personPrisaData.length : 0)
        ), 0)
        : 0;
      acc.maxPrisaYearsAllowed = Math.max(acc.maxPrisaYearsAllowed, prisaYears);
      acc.totalPrisaRev += (part.finalLinearBeforePrisa47 || 0) - (part.finalLinear47 || 0);
      acc.totalPrisaRev += (part.finalMutavBeforePrisa25 || 0) - (part.finalMutav25 || 0);
      acc.totalPrisaRev += (part.finalLinearBeforePrisa25 || 0) - (part.finalLinear25 || 0);
      acc.totalPrisaRev += (part.finalLinearBeforePrisa20 || 0) - (part.finalLinear20 || 0);
      acc.totalBeforePrisa += (part.finalLinearBeforePrisa47 || 0)
        + (part.finalMutavBeforePrisa25 || 0)
        + (part.finalLinearBeforePrisa25 || 0)
        + (part.finalLinearBeforePrisa20 || 0)
        + (part.finalInflationLinear10 || 0);
      acc.totalPreAfkaa += (part.finalLinearTaxAllPreAfkaa21 || 0) + (part.finalMutavTaxAllPreAfkaa21 || 0);
      return acc;
    },
    { totalPrisaRev: 0, maxPrisaYearsAllowed: 0, totalBeforePrisa: 0, totalPreAfkaa: 0 }
  );
}

function getAfkaaCredit(mainCalc) {
  return getDataPerPart(mainCalc).reduce(
    (sum, part) => sum + (part.finalLinearAfkaaZikui22 || 0) + (part.finalMutavAfkaaZikui22 || 0),
    0
  );
}

const HaklaiAnalysisResultsView = ({
  dealId,
  haklaiAnalysis,
  haklaiDraftPreCalc,
  analyzedPreCalc,
  analyzedMainCalc,
  onBackToCalc
}) => {
  const classes = useStyles();
  const rows = Array.isArray(haklaiAnalysis?.resultsByProperty) ? haklaiAnalysis.resultsByProperty : [];
  const summary = haklaiAnalysis?.summary || {};

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [dealId, haklaiAnalysis?.generatedAt]);

  const hasLossOffsets = Boolean(analyzedMainCalc?.kizuzIski || analyzedMainCalc?.kizuzHoni);
  const hasPrisa = analyzedMainCalc?.prisa?.shouldTryPrisa === true;
  const hasAfkaa = analyzedMainCalc?.afkaa === true;
  const rabinovichDiscountPercent = getMaximalRabinovich(analyzedMainCalc);
  const hasRabinovichDiscount = rabinovichDiscountPercent > 0;
  const prisaData = hasPrisa ? getHaklaiResultsPrisaData(analyzedMainCalc) : null;
  const businessLossOffset = Number(analyzedMainCalc?.kizuzIski || 0);
  const capitalLossOffset = Number(analyzedMainCalc?.kizuzHoni || 0);
  const showCalcDetails = hasLossOffsets || hasPrisa || hasAfkaa || hasRabinovichDiscount;
  const summaryRows = [
    {
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.totalShevach,
      value: formatCurrency(summary.totalShevach)
    },
    {
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.capitalGainsTax,
      value: formatCurrency(hasPrisa ? prisaData?.totalBeforePrisa : summary.capitalGainsTax)
    },
    {
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.totalTaxToPay,
      value: formatCurrency(summary.totalTaxToPay),
      isTotal: true
    }
  ];
  const calcDetailRows = [];

  if (businessLossOffset) {
    calcDetailRows.push({
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.businessLossOffset,
      value: formatCurrency(businessLossOffset)
    });
  }

  if (capitalLossOffset) {
    calcDetailRows.push({
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.capitalLossOffset,
      value: formatCurrency(capitalLossOffset)
    });
  }

  if (hasRabinovichDiscount) {
    calcDetailRows.push({
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.rabinovichDiscount,
      value: formatPercent(rabinovichDiscountPercent)
    });
  }

  if (hasPrisa) {
    calcDetailRows.push(
      {
        label: HAKLAI_ANALYSIS_RESULTS_LABELS.prisaYears,
        value: formatWholeNumber(prisaData?.maxPrisaYearsAllowed)
      },
      {
        label: HAKLAI_ANALYSIS_RESULTS_LABELS.prisaSavings,
        value: formatCurrency(prisaData?.totalPrisaRev)
      },
      {
        label: HAKLAI_ANALYSIS_RESULTS_LABELS.prisaTaxAfterPrisa,
        value: formatCurrency(prisaData?.totalPreAfkaa)
      }
    );
  }

  if (hasAfkaa) {
    calcDetailRows.push({
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.afkaaCredit,
      value: formatCurrency(getAfkaaCredit(analyzedMainCalc))
    });
  }
  return (
    <Box className={classes.page}>
      <Box className={classes.pageHeader}>
        <Typography variant="h5">{HAKLAI_ANALYSIS_RESULTS_LABELS.resultsTitle}</Typography>
        <Button variant="contained" color="secondary" className={classes.backButton} onClick={onBackToCalc}>
          <KeyboardArrowRightIcon className={classes.backButtonIcon} />
          <span>{HAKLAI_ANALYSIS_RESULTS_LABELS.backToCalc}</span>
        </Button>
      </Box>

      {!rows.length ? (
        <Card>
          <CardContent>
            <Typography variant="body1">
              {HAKLAI_ANALYSIS_RESULTS_MESSAGES.noResults}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <TableContainer className={classes.tableWrapper}>
            <Table size="small" className={classes.tableInner}>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    column.key === "propertyType" ? (
                      <StyledNameHeaderCell key={column.key} className={classes.propertyColumn}>
                        <Typography component="span" className={classes.headerLabelText}>
                          {column.label}
                        </Typography>
                      </StyledNameHeaderCell>
                    ) : (
                      <StyledHeaderCell key={column.key} className={classes.amountColumn}>
                        <Typography component="span" className={classes.headerLabelText}>
                          {column.label}
                        </Typography>
                      </StyledHeaderCell>
                    )
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const propertyExemptionLabel = getPropertyExemptionLabel(row, haklaiDraftPreCalc);
                  return (
                  <TableRow key={row.propertyKey} className={classes.resultRow}>
                    <StyledNameCell align="right" className={classes.propertyColumn}>
                      <Box className={classes.propertyTextBlock}>
                        <Typography variant="body2" className={classes.propertyMainText}>
                          {getPropertyDisplay(row)}
                        </Typography>
                        {propertyExemptionLabel ? (
                          <Typography variant="caption" className={classes.propertyMetaText}>
                            {propertyExemptionLabel}
                          </Typography>
                        ) : null}
                      </Box>
                    </StyledNameCell>
                    <StyledCell align="center">
                      <Typography variant="body2" className={`${classes.bodyText} ${classes.amountText}`}>
                        {formatCurrency(row.nominalAcquisitionPrice)}
                      </Typography>
                    </StyledCell>
                    <StyledCell align="center">
                      <Typography variant="body2" className={`${classes.bodyText} ${classes.amountText}`}>
                        {formatCurrency(row.indexedAcquisitionPrice)}
                      </Typography>
                    </StyledCell>
                    <StyledCell align="center">
                      <Typography variant="body2" className={`${classes.bodyText} ${classes.amountText}`}>
                        {formatCurrency(row.saleValue)}
                      </Typography>
                    </StyledCell>
                    <StyledCell align="center">
                      <Typography variant="body2" className={`${classes.bodyText} ${classes.amountText}`}>
                        {formatCurrency(row.indexedExpenses)}
                      </Typography>
                    </StyledCell>
                    <StyledCell align="center">
                      <Typography variant="body2" className={`${classes.bodyText} ${classes.amountText}`}>
                        {formatCurrency(row.depreciation)}
                      </Typography>
                    </StyledCell>
                    <StyledCell align="center">
                      <Typography variant="body2" className={`${classes.bodyText} ${classes.amountText} ${classes.totalText}`}>
                        {formatCurrency(row.payableTax)}
                      </Typography>
                    </StyledCell>
                  </TableRow>
                );})}
              </TableBody>
            </Table>
          </TableContainer>
          {showCalcDetails ? (
            <Box className={classes.calcDetailsSection}>
              <Box className={classes.resultTitleBar}>
                <Typography variant="subtitle1" className={classes.resultTitleText}>
                  {HAKLAI_ANALYSIS_RESULTS_LABELS.calcDetails}
                </Typography>
              </Box>
              <Box className={classes.calcDetailsCardsRow}>
                {calcDetailRows.map((row) => (
                  <Box className={classes.calcDetailsCard} key={row.label}>
                    <Typography variant="body2" className={classes.calcDetailsCardLabel}>
                      {row.label}
                    </Typography>
                    <Typography variant="h6" className={classes.calcDetailsCardValue}>
                      {row.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : null}

          <Box className={classes.summarySection}>
            <Box className={classes.resultTitleBar}>
              <Typography variant="subtitle1" className={classes.resultTitleText}>
                {HAKLAI_ANALYSIS_RESULTS_LABELS.summary}
              </Typography>
            </Box>
            <Box className={classes.summaryCardsRow}>
              {summaryRows.map((row) => (
                <Box
                  className={`${classes.summaryCard} ${row.isTotal ? classes.totalSummaryCard : ""}`}
                  key={row.label}
                >
                  <Typography variant="body2" className={classes.summaryCardLabel}>
                    {row.label}
                  </Typography>
                  <Typography variant="h6" className={`${classes.summaryCardValue} ${row.isTotal ? classes.totalText : ""}`}>
                    {row.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box className={classes.docsWrap}>
            <HaklaiResults2990Panel
              dealId={dealId}
              haklaiDraftPreCalc={haklaiDraftPreCalc}
              analyzedPreCalc={analyzedPreCalc}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default React.memo(HaklaiAnalysisResultsView);
